<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useCommandPalette, type Command } from '../composables/useCommandPalette'
import Dialog from 'primevue/dialog'

const { isOpen, searchQuery, groupedCommands, close, execute } = useCommandPalette()
const searchInput = ref<HTMLInputElement | null>(null)
const selectedIndex = ref(0)

// Focus input when dialog opens
watch(isOpen, async (val) => {
  if (val) {
    selectedIndex.value = 0
    await nextTick()
    searchInput.value?.focus()
  }
})

// Reset selection on search change
watch(searchQuery, () => {
  selectedIndex.value = 0
})

// Get flat list for keyboard navigation
function flatCommands(): Command[] {
  const flat: Command[] = []
  for (const cmds of Object.values(groupedCommands.value)) {
    flat.push(...cmds)
  }
  return flat
}

function handleKeydown(e: KeyboardEvent) {
  const flat = flatCommands()
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    selectedIndex.value = Math.min(selectedIndex.value + 1, flat.length - 1)
    scrollSelectedIntoView()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
    scrollSelectedIntoView()
  } else if (e.key === 'Enter' && flat[selectedIndex.value]) {
    e.preventDefault()
    execute(flat[selectedIndex.value])
  } else if (e.key === 'Escape') {
    close()
  }
}

function scrollSelectedIntoView() {
  nextTick(() => {
    const el = document.querySelector('.cmd-item--selected')
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function getGlobalIndex(cmd: Command): number {
  return flatCommands().indexOf(cmd)
}

// Global Cmd+K / Ctrl+K listener — skip when focused on inputs
function globalKeyHandler(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault()
    isOpen.value = !isOpen.value
  }
}

onMounted(() => window.addEventListener('keydown', globalKeyHandler))
onUnmounted(() => window.removeEventListener('keydown', globalKeyHandler))
</script>

<template>
  <Dialog
    v-model:visible="isOpen"
    modal
    :closable="false"
    :showHeader="false"
    :draggable="false"
    class="cmd-dialog"
    :pt="{
      root: { class: 'cmd-dialog-root' },
      mask: { class: 'cmd-dialog-mask' },
      content: { class: 'cmd-dialog-content' },
    }"
  >
    <div class="cmd-palette" @keydown="handleKeydown">
      <!-- Search bar -->
      <div class="cmd-search">
        <i class="pi pi-search cmd-search-icon"></i>
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="cmd-search-input"
          placeholder="Buscar comando..."
          autocomplete="off"
        />
        <kbd class="cmd-kbd">ESC</kbd>
      </div>

      <!-- Results -->
      <div class="cmd-results">
        <template v-if="flatCommands().length > 0">
          <div v-for="(cmds, category) in groupedCommands" :key="category" class="cmd-group">
            <div class="cmd-group-label">{{ category }}</div>
            <div
              v-for="cmd in cmds"
              :key="cmd.id"
              class="cmd-item"
              :class="{ 'cmd-item--selected': getGlobalIndex(cmd) === selectedIndex }"
              @click="execute(cmd)"
              @mouseenter="selectedIndex = getGlobalIndex(cmd)"
            >
              <i :class="cmd.icon" class="cmd-item-icon"></i>
              <span class="cmd-item-label">{{ cmd.label }}</span>
            </div>
          </div>
        </template>
        <div v-else class="cmd-empty">
          <i class="pi pi-search cmd-empty-icon"></i>
          <span>Nenhum resultado para "{{ searchQuery }}"</span>
        </div>
      </div>

      <!-- Footer hints -->
      <div class="cmd-footer">
        <span><kbd>&uarr;</kbd><kbd>&darr;</kbd> navegar</span>
        <span><kbd>Enter</kbd> executar</span>
        <span><kbd>Esc</kbd> fechar</span>
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.cmd-palette {
  display: flex;
  flex-direction: column;
  width: 500px;
  max-width: 90vw;
  background: var(--p-surface-0);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

:root.dark-mode .cmd-palette,
.dark-mode .cmd-palette {
  background: var(--p-surface-800);
}

/* Search bar */
.cmd-search {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--p-surface-200);
}

.dark-mode .cmd-search {
  border-bottom-color: var(--p-surface-700);
}

.cmd-search-icon {
  font-size: 1rem;
  color: var(--p-surface-400);
  flex-shrink: 0;
}

.cmd-search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 0.95rem;
  color: var(--p-surface-900);
  font-family: inherit;
}

.dark-mode .cmd-search-input {
  color: var(--p-surface-0);
}

.cmd-search-input::placeholder {
  color: var(--p-surface-400);
}

.cmd-kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  font-size: 0.7rem;
  font-family: inherit;
  font-weight: 600;
  color: var(--p-surface-500);
  background: var(--p-surface-100);
  border: 1px solid var(--p-surface-200);
  border-radius: 4px;
  line-height: 1.4;
}

.dark-mode .cmd-kbd {
  background: var(--p-surface-700);
  border-color: var(--p-surface-600);
  color: var(--p-surface-400);
}

/* Results area */
.cmd-results {
  max-height: 400px;
  overflow-y: auto;
  padding: 8px;
}

.cmd-results::-webkit-scrollbar {
  width: 4px;
}

.cmd-results::-webkit-scrollbar-track {
  background: transparent;
}

.cmd-results::-webkit-scrollbar-thumb {
  background: var(--p-surface-200);
  border-radius: 4px;
}

.dark-mode .cmd-results::-webkit-scrollbar-thumb {
  background: var(--p-surface-600);
}

/* Group */
.cmd-group {
  margin-bottom: 4px;
}

.cmd-group-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--p-surface-400);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 8px 4px;
}

/* Item */
.cmd-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--p-surface-700);
  transition: background 100ms ease, color 100ms ease;
}

.dark-mode .cmd-item {
  color: var(--p-surface-200);
}

.cmd-item:hover,
.cmd-item--selected {
  background: var(--p-primary-50);
  color: var(--p-primary-700);
}

.dark-mode .cmd-item:hover,
.dark-mode .cmd-item--selected {
  background: var(--p-primary-900);
  color: var(--p-primary-200);
}

.cmd-item-icon {
  font-size: 0.95rem;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.cmd-item-label {
  font-size: 0.875rem;
  font-weight: 500;
}

/* Empty state */
.cmd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  color: var(--p-surface-400);
  font-size: 0.875rem;
}

.cmd-empty-icon {
  font-size: 1.5rem;
  opacity: 0.5;
}

/* Footer */
.cmd-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid var(--p-surface-200);
  font-size: 0.7rem;
  color: var(--p-surface-400);
}

.dark-mode .cmd-footer {
  border-top-color: var(--p-surface-700);
}

.cmd-footer kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 4px;
  font-size: 0.65rem;
  font-family: inherit;
  font-weight: 600;
  color: var(--p-surface-500);
  background: var(--p-surface-100);
  border: 1px solid var(--p-surface-200);
  border-radius: 3px;
  margin: 0 2px;
  line-height: 1.4;
}

.dark-mode .cmd-footer kbd {
  background: var(--p-surface-700);
  border-color: var(--p-surface-600);
  color: var(--p-surface-400);
}
</style>

<style>
/* Global (unscoped) overrides for PrimeVue Dialog */
.cmd-dialog-root.p-dialog {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
  max-height: unset !important;
}

.cmd-dialog-root .p-dialog-content {
  padding: 0 !important;
  background: transparent !important;
  border-radius: 12px !important;
  overflow: visible !important;
}

.cmd-dialog-mask {
  backdrop-filter: blur(4px);
  align-items: flex-start !important;
  padding-top: 20vh !important;
}
</style>
